<template>
  <div>
    <ClinicPageHeader
      title="Acompanhamento"
      subtitle="Pacientes sem retorno, oportunidades e campanhas de reativação"
    >
      <template #actions>
        <button class="btn btn--outline btn--sm" @click="exportCsv">
          Exportar lista
        </button>
      </template>
    </ClinicPageHeader>

    <div class="dbody">
      <!-- Sub-navegação de acompanhamento -->
      <div class="tabs" style="margin-bottom:var(--space-6)">
        <router-link
          class="tab"
          active-class="tab--active"
          exact
          :to="{ name: 'ClinicNoReturn' }"
        >
          Sem retorno
        </router-link>
        <router-link
          class="tab"
          active-class="tab--active"
          exact
          :to="{ name: 'ClinicOpportunities' }"
        >
          Oportunidades
        </router-link>
        <router-link
          class="tab"
          active-class="tab--active"
          exact
          :to="{ name: 'ClinicCampaigns' }"
        >
          Campanhas
        </router-link>
      </div>

      <!-- Erro -->
      <ClinicStateBox
        v-if="state === 'error'"
        state="error"
        title="Não foi possível carregar a lista"
        message="Ocorreu um erro ao buscar os pacientes sem retorno. Tente novamente."
      >
        <template #actions>
          <button class="btn btn--primary btn--sm" @click="reload">
            Tentar de novo
          </button>
        </template>
      </ClinicStateBox>

      <!-- Loading -->
      <template v-else-if="state === 'loading'">
        <div
          class="seg"
          style="margin-bottom:var(--space-5);opacity:.5;pointer-events:none"
        >
          <a class="on">Todos</a>
        </div>
        <div class="card card--flush">
          <div style="padding:var(--space-5)">
            <div
              v-for="i in 6"
              :key="i"
              class="skel-line"
              style="width:100%;height:44px;margin-bottom:12px"
            />
          </div>
        </div>
      </template>

      <!-- Conteúdo -->
      <template v-else>
        <ClinicSeg
          :value="cutoff"
          :options="segOptions"
          style="margin-bottom:var(--space-5)"
          @input="selectCutoff"
        />

        <div class="dtoolbar">
          <div class="dsearch">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <circle
                cx="11"
                cy="11"
                r="7"
                stroke="currentColor"
                stroke-width="2"
              />
              <path
                d="M21 21l-4-4"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              />
            </svg>
            <input
              v-model="search"
              class="input"
              placeholder="Buscar paciente…"
            />
          </div>
          <select v-model="doctorFilter" class="select">
            <option value="">Todos os profissionais de saúde</option>
            <option v-for="d in doctorOptions" :key="d" :value="d">
              {{ d }}
            </option>
          </select>
          <div style="flex:1" />
          <router-link
            :to="{ name: 'ClinicCampaigns' }"
            class="btn btn--primary btn--sm"
          >
            Iniciar campanha de retorno →
          </router-link>
        </div>

        <div class="card card--flush">
          <table v-if="visiblePatients.length" class="table">
            <thead>
              <tr>
                <th style="width:34px">
                  <input
                    type="checkbox"
                    style="accent-color:var(--color-accent)"
                  />
                </th>
                <th>Paciente</th>
                <th>Última consulta</th>
                <th>Sem retorno há</th>
                <th>Profissional de saúde</th>
                <th>Especialidade</th>
                <th>Ação sugerida</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="p in visiblePatients"
                :key="p.id"
                class="tr--link"
                @click="goPatient(p)"
              >
                <td @click.stop>
                  <input
                    type="checkbox"
                    style="accent-color:var(--color-accent)"
                  />
                </td>
                <td>
                  <div class="row" style="gap:var(--space-3)">
                    <div class="avatar avatar--sm" :class="p.av">
                      {{ p.initials }}
                    </div>
                    <span class="cell-strong">{{ p.name }}</span>
                  </div>
                </td>
                <td class="num">{{ p.lastConsult }}</td>
                <td>
                  <span class="badge" :class="`badge--${p.badgeVariant}`">
                    {{ p.days }} dias
                  </span>
                </td>
                <td>{{ p.doctor }}</td>
                <td>{{ p.specialty || "—" }}</td>
                <td>
                  <div
                    class="row"
                    style="gap:var(--space-3);justify-content:space-between"
                  >
                    <span
                      style="font-size:var(--font-size-sm);color:var(--color-gray-600)"
                    >
                      {{ p.suggestion }}
                    </span>
                    <button
                      class="btn btn--outline btn--sm"
                      @click.stop="contact(p)"
                    >
                      Contatar
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <ClinicStateBox
            v-else
            state="empty"
            title="Ninguém neste corte"
            message="Nenhum paciente sem retorno no período selecionado."
          />

          <div
            v-if="visiblePatients.length"
            style="padding:var(--space-4) var(--space-5);border-top:1px solid var(--color-gray-100);display:flex;align-items:center;justify-content:space-between"
          >
            <span
              style="font-size:var(--font-size-xs);color:var(--color-gray-500)"
            >
              Lista gerada automaticamente · atualizada hoje, 06:00
            </span>
            <span
              style="font-size:var(--font-size-xs);color:var(--color-gray-500)"
            >
              <b style="color:var(--color-gray-800)">{{ totalNoReturn }}</b>
              pacientes sem retorno
            </span>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { ClinicFollowup } from "@/services/clinica";
import clinicaListQuery from "@/mixins/clinicaListQuery";
import ClinicPageHeader from "@/components/clinica/ClinicPageHeader.vue";
import ClinicStateBox from "@/components/clinica/ClinicStateBox.vue";
import ClinicSeg from "@/components/clinica/ClinicSeg.vue";

export default {
  name: "ClinicNoReturn",
  components: { ClinicPageHeader, ClinicStateBox, ClinicSeg },
  mixins: [clinicaListQuery],
  data() {
    return {
      // Estado de carga (loading/ready/error). O corte (cutoff) reflete na URL.
      state: "loading",
      cutoff: 30,
      counts: {},
      rows: [],
      search: "",
      doctorFilter: ""
    };
  },
  created() {
    this.cutoff = this.readCutoff();
    this.fetch();
  },
  methods: {
    /** Lê o corte da URL (default 30) como número. */
    readCutoff() {
      return Number(this.clinicaReadQuery({ cutoff: 30 }).cutoff) || 30;
    },
    async fetch() {
      this.state = "loading";
      try {
        const fail = this.$route.query.state === "error";
        const { data } = await ClinicFollowup.noReturn({
          cutoff: this.cutoff,
          unitId: this.selectedUnitId,
          fail
        });
        this.counts = data.counts;
        this.rows = data.items.map(p => this.mapRow(p));
        this.state = "ready";
      } catch (e) {
        this.state = "error";
      }
    },
    /** Corte veio do segmentado → grava na URL; o watch de $route.query refaz o fetch. */
    selectCutoff(value) {
      this.cutoff = value;
      this.clinicaSyncQuery({ cutoff: value });
    },
    reload() {
      this.fetch();
    },
    mapRow(p) {
      return {
        id: p.id,
        name: p.name,
        initials: p.initials,
        av: p.avatarClass,
        specialty: p.specialty || "",
        phone: p.phone,
        doctor: p.doctorName || "",
        lastConsult: this.formatDate(p.lastAppointment),
        days: p.daysSinceLast,
        badgeVariant: p.daysSinceLast >= 90 ? "danger" : "warning",
        suggestion: this.suggestionFor(p.daysSinceLast)
      };
    },
    // Aceita data pura (YYYY-MM-DD) ou timestamp ISO; exibe dd/mm/aaaa no fuso de Brasília.
    formatDate(iso) {
      if (!iso) return "—";
      if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
        const [y, m, d] = iso.split("-");
        return `${d}/${m}/${y}`;
      }
      const date = new Date(iso);
      if (isNaN(date)) return "—";
      return date.toLocaleDateString("pt-BR", {
        timeZone: "America/Sao_Paulo"
      });
    },
    suggestionFor(days) {
      if (days >= 90) return "Reativação";
      if (days >= 60) return "Retorno";
      return "Acompanhamento";
    },
    // Abre o WhatsApp (wa.me) com mensagem de reativação pronta.
    contact(p) {
      const digits = String(p.phone || "").replace(/\D/g, "");
      if (!digits) {
        this.$toast.error("Paciente sem telefone cadastrado");
        return;
      }
      const full = digits.length <= 11 ? `55${digits}` : digits;
      const first = (p.name || "").split(" ")[0];
      const doctor = p.doctor ? ` com ${p.doctor}` : "";
      const msg = `Olá ${first}! Já faz ${p.days} dias desde sua última consulta${doctor}. Vamos agendar um retorno?`;
      window.open(
        `https://wa.me/${full}?text=${encodeURIComponent(msg)}`,
        "_blank",
        "noopener"
      );
    },
    // CSV client-side da lista filtrada (BOM p/ Excel).
    buildCsv() {
      const header =
        "Nome;Telefone;Especialidade;Profissional de saúde;Última consulta;Dias;Sugestão";
      const lines = this.visiblePatients.map(p =>
        [
          p.name,
          p.phone || "",
          p.specialty,
          p.doctor,
          p.lastConsult,
          p.days,
          p.suggestion
        ].join(";")
      );
      return "\uFEFF" + [header, ...lines].join("\n");
    },
    exportCsv() {
      const blob = new Blob([this.buildCsv()], {
        type: "text/csv;charset=utf-8"
      });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `sem-retorno-${this.cutoff}d.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    },
    goPatient(p) {
      this.$router.push({
        name: "ClinicPatientProfile",
        params: { id: String(p.id) }
      });
    }
  },
  computed: {
    // Unidade selecionada no shell (contexto de filtro das listas).
    selectedUnitId() {
      return this.$store.getters["clinica/selectedUnitId"];
    },
    segOptions() {
      return [
        { value: 30, label: "+30 dias", count: this.counts[30] },
        { value: 60, label: "+60 dias", count: this.counts[60] },
        { value: 90, label: "+90 dias", count: this.counts[90] }
      ];
    },
    doctorOptions() {
      return [...new Set(this.rows.map(p => p.doctor))];
    },
    visiblePatients() {
      const term = this.search.trim().toLowerCase();
      return this.rows.filter(p => {
        const okDoctor = !this.doctorFilter || p.doctor === this.doctorFilter;
        const okSearch = !term || p.name.toLowerCase().includes(term);
        return okDoctor && okSearch;
      });
    },
    totalNoReturn() {
      return this.rows.length;
    }
  },
  watch: {
    selectedUnitId() {
      this.fetch();
    },
    "$route.query"() {
      this.cutoff = this.readCutoff();
      this.fetch();
    }
  }
};
</script>
